<template>
  <div>
    <LoadingSpinner :isLoading="loading" />
    <SoNote v-if="!loading && !mayEdit" tone="muted" icon="lock">{{ $t("securone.category_editor.no_write") }}</SoNote>

    <!-- after save: the reach panel (T21), informational, then back or continue -->
    <div v-else-if="!loading && savedId">
      <SoPageHead :title="form.name" :subtitle="$t('securone.category_editor.saved_heading')">
        <router-link :to="{ name: 'securone_categories' }" class="sd-btn sd-btn--secondary"><BaseIcon icon="chevron-left" class="mr-1" />{{ $t("securone.category_editor.back") }}</router-link>
      </SoPageHead>
      <ReachPanel :categoryId="savedId" class="mb-4" />
      <div class="flex flex-wrap gap-3">
        <router-link :to="{ name: 'securone_categories' }" class="sd-btn sd-btn--success">{{ $t("securone.category_editor.back_to_list") }}</router-link>
        <router-link :to="{ name: 'securone_category_edit', params: { id: savedId } }" class="sd-btn sd-btn--secondary" @click.native="savedId = null">{{ $t("securone.category_editor.continue_editing") }}</router-link>
      </div>
    </div>

    <div v-else-if="!loading">
      <SoPageHead :title="isNew ? $t('securone.category_editor.new_title') : form.name || ''" :subtitle="isNew ? $t('securone.category_editor.new_subtitle') : $t('securone.category_editor.edit_subtitle')">
        <router-link :to="{ name: 'securone_categories' }" class="sd-btn sd-btn--secondary"><BaseIcon icon="chevron-left" class="mr-1" />{{ $t("securone.category_editor.back") }}</router-link>
      </SoPageHead>

      <ValidationObserver ref="form">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          <div class="flex flex-col gap-4">
            <div class="sd-card px-5 py-4 flex flex-col gap-4">
              <div>
                <div class="text-sm font-semibold text-secondary-800 pb-2">{{ $t("securone.category_editor.icon") }}</div>
                <div class="flex flex-wrap gap-2">
                  <button v-for="key in iconKeys" :key="key" type="button" class="w-11 h-11 rounded border flex items-center justify-center focus:outline-none" :class="form.icon === key ? 'border-secondary-700 bg-secondary-50 text-secondary-800' : 'border-secondary-300 text-gray-500 hover:border-gray-400'" @click="form.icon = key" :title="key">
                    <BaseIcon :icon="categoryIcon(key)" class="text-lg" />
                  </button>
                </div>
              </div>
              <BaseInput v-model="form.name" :field_name="$t('securone.category_editor.name')" rules="required" :placeholder="$t('securone.category_editor.name_placeholder')" />
              <div>
                <BaseTextarea v-model="form.instruction" :field_name="$t('securone.category_editor.instruction')" rules="required" rows="3" :placeholder="$t('securone.category_editor.instruction_placeholder')" />
                <div class="text-xs text-gray-500 mt-1">{{ $t("securone.category_editor.instruction_help") }}</div>
              </div>
              <div>
                <BaseInput v-model="form.resolveCode" :field_name="$t('securone.category_editor.resolve_code')" rules="required" autocomplete="off" />
                <div class="text-xs text-gray-500 mt-1">{{ $t("securone.category_editor.resolve_code_help") }}</div>
              </div>
              <BaseSwitch v-if="!isNew" v-model="form.isActive" :field_name="$t('securone.category_editor.active')" />
            </div>
            <div class="sd-card px-5 py-4">
              <ScopeSelector v-model="scope" wide="customer" capability="customerCategory" :help="$t('securone.category_editor.scope_help')" />
            </div>
          </div>

          <div class="sd-card px-5 py-4">
            <div class="text-sm font-semibold text-secondary-800 pb-2">{{ $t("securone.category_editor.groups_title") }}</div>
            <div class="border border-secondary-200 rounded divide-y divide-secondary-100">
              <!-- V6: only groups the category's scope permits are offered -->
              <label v-for="g in linkableGroups" :key="g._id" class="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50" :class="{ 'bg-secondary-50': form.groups.includes(g._id) }">
                <input type="checkbox" class="form-checkbox h-4 w-4" :value="g._id" v-model="form.groups" />
                <div class="flex-1 min-w-0">
                  <div class="text-sm text-gray-800">{{ g.name }}</div>
                  <div class="text-xs text-gray-500">{{ $tc("securone.common.members_count", g.memberCount, { n: g.memberCount }) }} · {{ scopeLabel(g) }}</div>
                </div>
              </label>
              <div v-if="!linkableGroups.length" class="px-3 py-4 text-sm text-gray-400">{{ groups.length ? $t("securone.category_editor.group_not_linkable") : $t("securone.category_editor.no_groups_yet") }}</div>
            </div>
            <div class="text-xs text-gray-500 mt-2">{{ $t("securone.category_editor.groups_created_under_groups") }}</div>

            <div class="text-gray-600 text-sm font-bold pb-2 pt-5 font-serif">{{ $t("securone.category_editor.documents_title") }}</div>
            <div class="border border-secondary-200 rounded divide-y divide-secondary-100">
              <label v-for="d in documents" :key="d._id" class="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50" :class="{ 'bg-secondary-50': form.documents.includes(d._id) }">
                <input type="checkbox" class="form-checkbox h-4 w-4" :value="d._id" v-model="form.documents" />
                <div class="flex-1 min-w-0">
                  <div class="text-sm text-gray-800">{{ d.title }}</div>
                  <div class="text-xs text-gray-500"><span class="font-mono">v{{ d.currentVersion }}</span> · {{ dateStamp(d.updatedAt) }} · {{ levelLabel(d) }}</div>
                </div>
              </label>
              <div v-if="!documents.length" class="px-3 py-4 text-sm text-gray-400">{{ $t("securone.category_editor.no_documents_yet") }}</div>
            </div>
            <div class="text-xs text-gray-500 mt-2">{{ $t("securone.category_editor.documents_uploaded_under_documents") }}</div>
          </div>
        </div>
      </ValidationObserver>

      <div class="flex flex-wrap items-center gap-4 rounded bg-secondary-50 border border-secondary-200 px-5 py-3 mt-4">
        <BaseIcon icon="users" class="text-secondary-500" />
        <div class="text-sm text-gray-800">
          <template v-if="form.groups.length">{{ $t("securone.category_editor.reaches") }} {{ reachLine }}</template>
          <span v-else class="text-red-600">{{ $t("securone.category_editor.reaches_nobody") }}</span>
        </div>
        <div class="ml-auto text-xs text-gray-600">{{ form.documents.length ? $tc("securone.category_editor.documents_attached", form.documents.length, { n: form.documents.length }) : $t("securone.category_editor.no_document_attached") }}</div>
      </div>

      <div class="flex flex-wrap items-center gap-3 mt-4">
        <button class="sd-btn sd-btn--success" :disabled="saving" @click="save">{{ isNew ? $t("securone.category_editor.create_and_publish") : $t("securone.category_editor.save_and_publish") }}<BaseIcon icon="save" class="ml-1" /></button>
        <router-link :to="{ name: 'securone_categories' }" class="sd-btn sd-btn--secondary">{{ $t("securone.common.cancel") }}</router-link>
        <div class="text-xs text-gray-500" style="max-width: 44ch">{{ $t("securone.category_editor.publish_help") }}</div>
        <button v-if="!isNew && !(category && category.hasActiveEvent)" class="sd-btn sd-btn--danger ml-auto" @click="confirmDelete"><BaseIcon icon="trash" class="mr-1" />{{ $t("securone.category_editor.delete") }}</button>
      </div>
      <SoNote v-if="category && category.hasActiveEvent" tone="warn" class="mt-3">{{ $t("securone.category_editor.delete_blocked") }}</SoNote>
    </div>
  </div>
</template>

<script>
import SoPageHead from "./components/SoPageHead.vue";
import SoNote from "./components/SoNote.vue";
import ScopeSelector from "./components/ScopeSelector.vue";
import ReachPanel from "./components/ReachPanel.vue";
import api from "./securone.api";
import { categoryIcon, CATEGORY_ICON_KEYS, dateStamp, errorMessage, scopeLabel, escapeHtml } from "./securone.helpers";

// D-29. Scope selector governs later edit rights and what the group list
// offers (V6). Live reach is recomputed as groups are ticked. Links live on
// the category (I1). After saving, the reach panel (T21). Delete is refused
// while a broadcast of the category is open.
export default {
  name: "SecurOneCategoryEditor",
  components: { SoPageHead, SoNote, ScopeSelector, ReachPanel },
  data() {
    return {
      loading: true,
      saving: false,
      savedId: null,
      category: null,
      groups: [],
      documents: [],
      iconKeys: CATEGORY_ICON_KEYS,
      form: { name: "", icon: "megaphone", instruction: "", resolveCode: "", groups: [], documents: [], isActive: true },
      scope: { scope: "departments", departmentIds: [] },
    };
  },
  computed: {
    isNew() {
      return this.$route.name === "securone_category_new";
    },
    canWrite() {
      return this.$store.getters["securone/canWrite"];
    },
    mayEdit() {
      return this.isNew ? !!this.canWrite.category : !!(this.category && this.category.canEdit);
    },
    // V6, client side (the server decides): a customer-scoped category may link
    // any group; a department-scoped one only groups inside its departments.
    linkableGroups() {
      if (this.scope.scope === "customer") return this.groups;
      const mine = (this.scope.departmentIds || []).map(String);
      return this.groups.filter((g) => g.scope === "departments" && g.departmentIds.every((id) => mine.includes(String(id))));
    },
    selectedGroups() {
      return this.groups.filter((g) => this.form.groups.includes(g._id));
    },
    reachLine() {
      const people = new Set(), sms = new Set();
      this.selectedGroups.forEach((g) => {
        (g.members || []).forEach((m) => people.add(String(m)));
        (g.smsContacts || []).forEach((s) => sms.add(String(s)));
      });
      let line = this.$tc("securone.common.people_in_groups", this.selectedGroups.length, { people: people.size, groups: this.selectedGroups.length });
      if (sms.size) line += " · " + this.$tc("securone.common.sms_contacts_count", sms.size, { n: sms.size });
      return line;
    },
  },
  methods: {
    categoryIcon,
    dateStamp,
    scopeLabel(g) {
      return scopeLabel(this, g.scope, g.departmentNames);
    },
    levelLabel(d) {
      return scopeLabel(this, d.level, d.departmentNames);
    },
    async load() {
      this.loading = true;
      try {
        const [groups, documents, categories] = await Promise.all([api.groups(), api.documents(), this.isNew ? [] : api.categories()]);
        this.groups = groups;
        this.documents = documents;
        if (!this.isNew) {
          this.category = categories.find((c) => c._id === this.$route.params.id) || null;
          if (!this.category) return this.$router.replace({ name: "securone_categories" });
          const c = this.category;
          this.form = { name: c.name, icon: c.icon || "megaphone", instruction: c.instruction || "", resolveCode: c.resolveCode || "", groups: (c.groups || []).map(String), documents: (c.documents || []).map(String), isActive: c.isActive !== false };
          this.scope = { scope: c.scope, departmentIds: (c.departmentIds || []).map(String) };
        } else if (!this.canWrite.customerCategory) {
          this.scope = { scope: "departments", departmentIds: [] };
        }
      } catch (error) {
        this.handleError(error);
      } finally {
        this.loading = false;
      }
    },
    async save() {
      if (!(await this.$refs.form.validate())) return;
      if (this.scope.scope === "departments" && !this.scope.departmentIds.length) return;
      this.saving = true;
      try {
        // groups the new scope no longer permits are dropped from the link list (V6)
        const linkable = this.linkableGroups.map((g) => g._id);
        const body = { ...this.form, groups: this.form.groups.filter((id) => linkable.includes(id)), isActive: this.form.isActive !== false, scope: this.scope.scope, departmentIds: this.scope.departmentIds };
        const saved = this.isNew ? await api.createCategory(body) : await api.updateCategory(this.category._id, body);
        this.$toasted.show(body.groups.length ? this.$t("securone.category_editor.published_toast") : this.$t("securone.category_editor.published_no_groups_toast"), { type: "success", theme: "outline", position: "top-right", duration: 6000 });
        this.savedId = String(saved._id);
      } catch (error) {
        this.handleError(error, errorMessage(this, error));
      } finally {
        this.saving = false;
      }
    },
    confirmDelete() {
      const c = this.category;
      this.$modal.show("dialog", {
        title: `<span class="text-red-500">${this.$t("securone.category_editor.delete_title", { name: escapeHtml(c.name) })}</span>`,
        text: `<div class="text-sm text-gray-700 text-left">${this.$t("securone.category_editor.delete_consequence")}<br><br><span class="text-gray-500">${this.$t("securone.category_editor.currently_reaches", { reach: escapeHtml(this.reachLine) })}</span></div>`,
        buttons: [
          { title: `<div class="bg-success-500 text-white text-sm font-sans py-2">${this.$t("securone.common.cancel")}</div>`, handler: () => this.$modal.hide("dialog") },
          { title: `<div class="bg-red-500 text-white text-sm font-sans py-2">${this.$t("securone.category_editor.delete")}</div>`, handler: () => { this.$modal.hide("dialog"); this.remove(); } },
        ],
      });
    },
    async remove() {
      try {
        await api.deleteCategory(this.category._id);
        this.$toasted.show(this.$t("securone.category_editor.deleted_toast"), { type: "success", theme: "outline", position: "top-right", duration: 5000 });
        this.$router.push({ name: "securone_categories" });
      } catch (error) {
        this.handleError(error, errorMessage(this, error));
      }
    },
  },
  watch: {
    "$route.name"() {
      this.savedId = null;
      this.load();
    },
  },
  created() {
    this.setPageTitle("SecurOne · " + this.$t("securone.nav.categories"), this.$route.name);
    this.load();
  },
};
</script>
